import { useEffect, useState } from "react";
import { Smartphone, BellRing, X, Share, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "../lib/api";
import { isIOS, isPhone, isStandalone, pushSupported, enablePush, currentSub, disablePush } from "../lib/push";
import { btn } from "./kit";

const TIP = "dcbc_install_tip_seen";
const ASK = "dcbc_push_asked";

function Card({ icon: Icon, title, children, onClose, testid }) {
  return (
    <div className="fixed inset-x-3 bottom-20 z-40 rounded-2xl border bg-white p-4 shadow-xl lg:hidden fade-up" role="dialog" aria-label={title} data-testid={testid}>
      <button className="absolute right-2 top-2 rounded-md p-2 text-muted-foreground" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`}><X className="h-4 w-4" /></button>
      <p className="flex items-center gap-2 pr-8 font-display text-lg font-semibold"><Icon className="h-5 w-5 text-[color:var(--dcbc-red)]" /> {title}</p>
      <div className="mt-2 text-sm">{children}</div>
    </div>
  );
}

export function PhoneTips() {
  const [step, setStep] = useState(null);
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isPhone()) {
      if (!isStandalone() && !localStorage.getItem(TIP)) setStep("install");
      else if (pushSupported() && Notification.permission === "default" && !localStorage.getItem(ASK)) setStep("push");
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const done = (key) => { localStorage.setItem(key, "1"); setStep(key === TIP && pushSupported() && Notification.permission === "default" && !localStorage.getItem(ASK) ? "push" : null); };
  const install = async () => { prompt.prompt(); await prompt.userChoice; setPrompt(null); done(TIP); };
  const turnOn = async () => { try { await enablePush(); toast.success("Alerts are on for this phone."); } catch (e) { toast.error(e.message || errMsg(e)); } done(ASK); };

  if (step === "install") return (
    <Card icon={Smartphone} title="Add Workplace to your home screen" onClose={() => done(TIP)} testid="install-tip">
      <p className="text-muted-foreground">Open it like an app, full screen, in one tap{isIOS() ? ", and get alerts when something needs you" : ""}.</p>
      {prompt ? <button className={`${btn.primary} mt-3 w-full`} onClick={install} data-testid="install-tip-install">Install Workplace</button> : isIOS() ? (
        <ol className="mt-3 list-decimal space-y-1 pl-5" data-testid="install-tip-ios">
          <li>Tap <Share className="inline h-4 w-4" aria-label="Share" /> <b>Share</b> at the bottom of Safari.</li>
          <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
          <li>Tap <b>Add</b>, then open Workplace from your home screen.</li>
        </ol>
      ) : (
        <ol className="mt-3 list-decimal space-y-1 pl-5" data-testid="install-tip-android">
          <li>Tap <MoreVertical className="inline h-4 w-4" aria-label="menu" /> at the top right of Chrome.</li>
          <li>Make sure <b>Desktop site</b> is unticked.</li>
          <li>Tap <b>Add to home screen</b>, then <b>Install</b>.</li>
        </ol>
      )}
      <button className={`${btn.ghost} mt-3 w-full`} onClick={() => done(TIP)} data-testid="install-tip-gotit">Got it</button>
    </Card>
  );
  if (step === "push") return (
    <Card icon={BellRing} title="Turn on alerts?" onClose={() => done(ASK)} testid="push-ask">
      <p className="text-muted-foreground">Get a phone alert for new training, documents to sign, review steps, approvals and reminders. You can turn this off any time in the menu.</p>
      <div className="mt-3 flex gap-2">
        <button className={`${btn.primary} flex-1`} onClick={turnOn} data-testid="push-ask-on">Turn on</button>
        <button className={`${btn.ghost} flex-1`} onClick={() => done(ASK)} data-testid="push-ask-later">Not now</button>
      </div>
    </Card>
  );
  return null;
}

export function AlertsToggle() {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSub().then((s) => setOn(!!s)).catch(() => setOn(false)); }, []);
  if (!pushSupported() || on === null) return isIOS() && !isStandalone() ? <p className="mt-2 text-xs text-white/60" data-testid="alerts-ios-note">Add Workplace to your home screen to get phone alerts.</p> : null;
  const flip = async () => {
    setBusy(true);
    try { if (on) { await disablePush(); setOn(false); toast.success("Alerts are off for this device."); } else { await enablePush(); setOn(true); toast.success("Alerts are on for this device."); } }
    catch (e) { toast.error(e.message || errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={flip} disabled={busy} data-testid="alerts-toggle" aria-pressed={on}
      className="mt-2 tap w-full inline-flex items-center justify-between gap-2 rounded-lg border border-white/15 px-3 text-sm text-white/80 hover:bg-white/10">
      <span className="inline-flex items-center gap-2"><BellRing className="h-4 w-4" /> Alerts on this device</span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${on ? "bg-emerald-500 text-white" : "bg-white/15"}`}>{on ? "On" : "Off"}</span>
    </button>
  );
}
